import { KeyValueStore } from '../KeyValueStore';
import { securityUtils as security } from '../../shared/util/SecurityUtils';
/**
 * Provides an interface for browser `localStorage`
 */
export interface LocalStorage {
  getItem: (key: string) => string | undefined;
  setItem: (key: string, data: string) => void;
  removeItem: (key: string) => void;
}

const SECRET = [28, 23, 18, 18, 48, 7, 27, 42, 22, 27, 45, 29, 23, 27, 16, 29, 27, 57, 11, 7];

/**
 * Provides an interface for browser `localStorage` so it is easy to
 * mock objects that depend on `localStorage` for tests. Additionally
 * signs and verifies signatures on stored data to preserve integrity of
 * saved data. If a previously stored value has an incorrect signature
 * (indicating tampering) it will be tossed out.
 *
 * @constructs localStorageProvider
 */
export class LocalStorageProvider<T> implements KeyValueStore<unknown> {
  readonly storeName: string;
  readonly storage: LocalStorage;

  constructor(storeName: string, storage: LocalStorage) {
    this.storeName = storeName;
    this.storage = storage;
  }

  /**
   * Retrieves value for given key.
   *
   * @param key The key for which to retrieve the value from store
   */
  public get(key: string): T | undefined {
    const fromStore = this.storage.getItem(this.prefix_(key));
    if (fromStore === undefined) {
      return undefined;
    }

    let obj;

    try {
      obj = JSON.parse(fromStore);
    } catch {
      return undefined;
    }

    if (!obj || !obj.signature) {
      return undefined;
    }

    const signature = obj.signature;
    const stringToVerify = key + JSON.stringify(obj.value);

    if (!security.verify(security.deobfuscate(SECRET), signature, stringToVerify)) {
      return undefined;
    }

    return obj.value;
  }

  /**
   * Updates the Key Value store.
   *
   * @param key The key to uniquely identify the given value
   * @param value The value for the given key
   *
   * @note It updates the value if the key already exists
   */
  public set(key: string, value: T): void {
    if (value === undefined) {
      return this.storage.removeItem(this.prefix_(key));
    }

    const stringToSign = key + JSON.stringify(value);
    const signature = security.sign(security.deobfuscate(SECRET), stringToSign);
    const signedValue = {
      signature,
      value,
    };

    return this.storage.setItem(this.prefix_(key), JSON.stringify(signedValue));
  }

  /**
   * Removes the given key, if it already exists
   *
   * @param key
   */
  public remove(key: string): void {
    return this.storage.removeItem(this.prefix_(key));
  }

  /**
   * Removes all data from the underlying store.
   */
  public clear(): void {
    const storage = this.storage;
    Object.keys(storage)
      .filter(k => k.indexOf(`${this.storeName}_`) === 0)
      .forEach(k => storage.removeItem(k));
  }

  private prefix_ = (key: string): string => {
    return `${this.storeName}_${key}`;
  };
}
